import { ApiError, ProviderErrorResponse, UnauthorizedError } from './errors.js';

/**
 * The schedule errors both clients share, minus the save/import/push errors
 * (`ScheduleSaveError`, `ScheduleAlreadyImportedError`): those serve
 * scheduler-only operations that stay web-side. The classes live here so an
 * `instanceof` check matches the same identity in both apps (see errors.ts).
 */

/**
 * A GET/PUT hit a schedule that has never been seeded into D-ORCA. The page
 * offers a one-click import rather than a generic load error. Carries the
 * scheduleNumber so the import affordance can target it.
 */
export class ScheduleNotImportedError extends Error {
  constructor(public readonly scheduleNumber: string, message: string) {
    super(message);
    this.name = 'ScheduleNotImportedError';
  }
}

/**
 * The site can't be scheduled: either it isn't a known D-ORCA site (e.g. a stale
 * working-site id — `not-found`) or it has no linked Genba site (`not-linked`).
 * The page shows a "pick a site" state rather than a generic load error.
 */
export class ScheduleSiteUnavailableError extends Error {
  constructor(public readonly reason: 'not-found' | 'not-linked', message: string) {
    super(message);
    this.name = 'ScheduleSiteUnavailableError';
  }
}

/** TanStack Query `retry` policy shared by every schedule-backed query (the
 *  dashboard cards and the TopBar picker): don't retry expected "can't schedule
 *  this site" / provider-reauth states; otherwise allow up to two retries. */
export function scheduleRetry(failureCount: number, err: unknown): boolean {
  if (err instanceof ProviderErrorResponse || err instanceof ScheduleSiteUnavailableError) return false;
  return failureCount < 2;
}

/**
 * A 409/404 SCHEDULE_NOT_IMPORTED → a typed error the page can act on. Returns
 * the error to throw (so the caller controls the throw site), or null if the
 * response isn't a not-imported one. Clones so the body can still be read by the
 * shared error handler.
 */
export async function throwForNotImported(res: Response, scheduleNumber: string): Promise<ScheduleNotImportedError | null> {
  if (res.status !== 409 && res.status !== 404) return null;
  const body = await res.clone().json().catch(() => null);
  if (body?.error?.code === 'SCHEDULE_NOT_IMPORTED') {
    return new ScheduleNotImportedError(scheduleNumber, body?.error?.message ?? 'schedule not imported');
  }
  return null;
}

export async function throwForError(res: Response, what: string): Promise<never> {
  const body = await res.json().catch(() => null);
  const code: string | undefined = body?.error?.code;
  const provider = body?.error?.details?.provider;
  if (res.status === 401) {
    // Genba has no refresh token, so an expired session surfaces as a 401 with
    // this code — show the reconnect prompt, not a generic D-ORCA logout. A 401
    // without it is a real session failure. (Mirrors apis/documents.ts.)
    if (code === 'PROVIDER_REAUTH_REQUIRED' && provider) throw new ProviderErrorResponse(code, provider);
    throw new UnauthorizedError();
  }
  if (res.status === 409 && (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')) {
    throw new ProviderErrorResponse(code, provider);
  }
  if (res.status === 404 && code === 'SITE_NOT_FOUND') {
    throw new ScheduleSiteUnavailableError('not-found', body?.error?.message ?? 'site not found');
  }
  if (res.status === 409 && code === 'GENBA_SITE_NOT_LINKED') {
    throw new ScheduleSiteUnavailableError('not-linked', body?.error?.message ?? 'site not linked to Genba');
  }
  throw new ApiError(res.status, code, `${what} failed: ${res.status} ${body?.error?.message ?? ''}`, body?.error?.details);
}
